import React, { useState, useEffect, useRef } from 'react';
import { ORDER_STATUS, ORDER_STATUS_COLORS } from '../orderStatus';
import { api } from '../api';
import { useCatalogLabels, singularLower, capitalizeFirst } from '../catalogLabels';
import { BRAND_NAME } from '../brand';
import { formatOrderPackaging } from '../utils/packagingDisplay';
import Modal from './Modal';
import { nfConfirm } from './confirm';
import OrderEditPanel from './OrderEditPanel';
import ProductSearchDropdown from './ProductSearchDropdown';
import Select from './Select';
import TimeCombo from './TimeCombo';
import ClientProfileModal from './ClientProfileModal';
import { formatPhoneDisplay, phoneTelHref } from '../utils';
import { paymentMethodLabel } from '../utils/paymentMethod';
import InlinePaymentDialog from './InlinePaymentDialog';
import FlowwowOrderPanel, { flowwowCustomerName } from './FlowwowOrderPanel';
import SlotChip from './SlotChip';
import OrderPhotos from './OrderPhotos';
import QtyStepper from './QtyStepper';
import { EditIcon, TrashIcon } from './ActionIcons';

const statusLabels = ORDER_STATUS; // единые подписи (src/orderStatus.js)

const paymentStatusLabels = {
  pending: { label: 'Ожидает', class: 'pending' },
  paid: { label: 'Оплачен', class: 'completed' },
  paid_chat: { label: 'Оплачен', class: 'completed' },
  failed: { label: 'Ошибка оплаты', class: 'cancelled' },
  expired: { label: 'Истёк срок оплаты', class: 'cancelled' },
  cancelled: { label: 'Отменён', class: 'cancelled' },
  not_required: { label: 'Не требуется', class: 'info' },
};

const PAYMENT_EXPIRE_MINUTES = 60;

/**
 * OrderModal — переиспользуемый компонент модального окна заказа.
 * Props:
 *   initialOrder   — объект заказа (должен быть загружен через api.getOrderById)
 *   onClose        — функция закрытия модала
 *   showToast      — функция показа уведомлений
 *   onOrderUpdated — (необязательно) колбэк после изменения статуса/оплаты
 */
// Поверхности карточки заказа (спецификация дизайнера 07.10): секция — белая карточка
// с рамкой и срезом одного цвета; «внимание» (ждёт доплата, не оплачен) — персиковая.
// Цветных полос слева и цветных фонов у обычных секций нет.
const SECTION = { padding: '14px 16px', background: '#fff', border: '1px solid #D8DDDA', '--ch': '10px', '--stroke': '#D8DDDA' };
const SECTION_ATTN = { ...SECTION, background: '#FFF4F0', border: '1px solid #ED956F', '--stroke': '#ED956F' };
// Заголовок секции — светло-терракотовая полоса во всю ширину верха карточки (срез угла режет и её)
const SECTION_HEAD = { margin: '-14px -16px 12px', padding: '9px 16px', background: '#FFE7DD', color: '#1C3D2E', fontSize: 11, lineHeight: '16px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6 };

// Позиция заказа с площадки: наше название и, если отличается, как её видел
// покупатель на Flowwow — «04. — Букет из калл и роз №4». Учёт — по нашему названию.
const normName = (v) => (v || '').split(/\s+/).filter(Boolean).join(' ').toLowerCase();
const itemTitle = (it) => {
  const ext = (it.external_name || '').trim();
  return ext && normName(ext) !== normName(it.product_name) ? `${it.product_name} — ${ext}` : it.product_name;
};

function OrderModal({ initialOrder, onClose, showToast, onOrderUpdated, onBeforeStatusChange, onBeforeCancellation }) {
  const catalogLabels = useCatalogLabels();
  const [selectedOrder, setSelectedOrder] = useState(initialOrder);
  // Текст ошибки окна доставки ('' = всё в порядке): время вне слота или «не раньше» ≥ «не позже».
  // Пока не пусто — сохранение блокируется и текст уходит в тост.
  const windowInvalidRef = useRef('');
  const [editForm, setEditForm] = useState({
    status: initialOrder.status,
    admin_comment: initialOrder.admin_comment || '',
    courier_comment: initialOrder.courier_comment || '',
    deliver_from: initialOrder.deliver_from || '',
    deliver_to: initialOrder.deliver_to || '',
    cancellationReason: initialOrder.cancellation_comment || '',
    courier_id: initialOrder.courier_id || null,
    florist_id: initialOrder.florist_id || null,
    packer_id: initialOrder.packer_id || null,
  });
  const [pendingCancellation, setPendingCancellation] = useState(false);
  const [returnToStock, setReturnToStock] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // Клик внутри редактирования (добавили позицию, «+/−») — изменение без событий ввода:
  // Modal сам его не видит, и клик мимо окна закрыл бы карточку без вопроса
  const [editTouched, setEditTouched] = useState(false);
  useEffect(() => { if (!isEditing) setEditTouched(false); }, [isEditing]);
  // Ref на OrderEditPanel — чтобы триггерить его handleSave из footer-кнопки модалки
  const editPanelRef = useRef(null);
  const [editSaving, setEditSaving] = useState(false);
  const [couriers, setCouriers] = useState([]);
  const [florists, setFlorists] = useState([]);
  const [packers, setPackers] = useState([]);
  const [packerWorkflowEnabled, setPackerWorkflowEnabled] = useState(false);
  const [photoControlEnabled, setPhotoControlEnabled] = useState(false);
  // paymentForm-state теперь живёт внутри InlinePaymentDialog
  const [payAllSubscription, setPayAllSubscription] = useState(true);
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(null);
  const [adjForm, setAdjForm] = useState({ method: '', paid_at_date: '', paid_at_time: '', comment: '', editTime: false, error: '' });
  const [cancelAdjConfirm, setCancelAdjConfirm] = useState(null); // adj object to cancel
  const [noPaymentAdj, setNoPaymentAdj] = useState(null); // adj to close без доплаты
  const [noPaymentComment, setNoPaymentComment] = useState('');
  const [noPaymentError, setNoPaymentError] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [commentsSaving, setCommentsSaving] = useState(false);
  const [clientModalId, setClientModalId] = useState(null);
  // Наполнение заказа подписки
  const [fillItems, setFillItems] = useState([]);
  const [showFillSearch, setShowFillSearch] = useState(false);
  const [isFilling, setIsFilling] = useState(false);
  const [fillMaxQtyMap, setFillMaxQtyMap] = useState({});
  const [fillFeePercent, setFillFeePercent] = useState(11);
  const [fillEditMode, setFillEditMode] = useState(false);

  useEffect(() => {
    api.getSettings().then(s => {
      if (s.service_fee_percent) setFillFeePercent(Number(s.service_fee_percent));
    }).catch(() => {});
    api.getEmployees('courier').then(setCouriers).catch(() => {});
    api.getEmployees('florist').then(setFlorists).catch(() => {});
    api.getEmployees('packer').then(setPackers).catch(() => {});
    api.getPackerWorkflowEnabled().then(r => setPackerWorkflowEnabled(r.enabled)).catch(() => {});
    api.getPhotoControlEnabled().then(r => setPhotoControlEnabled(!!r.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    setSelectedOrder(initialOrder);
    setEditForm({
      status: initialOrder.status,
      admin_comment: initialOrder.admin_comment || '',
      courier_comment: initialOrder.courier_comment || '',
      // Окно доставки обязательно восстанавливать: «Сохранить» внизу окна
      // шлёт deliver_from/deliver_to всегда, и пустая строка на бэке = очистить окно.
      deliver_from: initialOrder.deliver_from || '',
      deliver_to: initialOrder.deliver_to || '',
      cancellationReason: initialOrder.cancellation_comment || '',
      courier_id: initialOrder.courier_id || null,
      florist_id: initialOrder.florist_id || null,
      packer_id: initialOrder.packer_id || null,
    });
    setPendingCancellation(false);
    setShowPaymentDialog(false);
    setShowAdjustmentDialog(null);
    setShowHistoryModal(false);
    setIsEditing(false);
  }, [initialOrder?.id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const handleFillOrder = async () => {
    setIsFilling(true);
    try {
      await api.fillSubscriptionOrder(
        selectedOrder.subscription_id,
        selectedOrder.id,
        fillItems.map(i => ({ product_id: i.id, quantity: i.quantity }))
      );
      showToast(fillItems.length > 0 ? 'Заказ наполнен' : 'Состав очищен');
      const updated = await api.getOrderById(selectedOrder.id);
      setSelectedOrder(updated);
      setFillItems([]);
      setShowFillSearch(false);
      setFillEditMode(false);
      if (onOrderUpdated) onOrderUpdated();
    } catch (error) {
      showToast(error.message || 'Ошибка наполнения', 'error');
    } finally {
      setIsFilling(false);
    }
  };

  // Заказ перечитан с сервера. Заметку, комментарий курьеру и окно, которые админ не трогал, берём
  // новые: пересчёт адреса Flowwow убирает из заметки строку «⚠️ Адрес», правка двигает окно — а
  // форма держала старое, и внизу всплывало «Отменить / Сохранить» без всяких правок (25.10)
  const applyServerOrder = (updated) => {
    setEditForm(prev => {
      const next = { ...prev, status: updated.status };
      ['admin_comment', 'courier_comment', 'deliver_from', 'deliver_to'].forEach(k => {
        if ((prev[k] || '') === (selectedOrder[k] || '')) next[k] = updated[k] || '';
      });
      return next;
    });
    setSelectedOrder(updated);
  };

  const reloadOrder = async () => {
    const updated = await api.getOrderById(selectedOrder.id).catch(() => null);
    if (updated) {
      applyServerOrder(updated);
    }
    if (onOrderUpdated) onOrderUpdated();
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    const prevStatus = selectedOrder.status;
    // Фото-контроль (или канал продаж требует фото, как Flowwow): первый шаг после «Новый»
    // («на упаковку» или «Собран») только с фото
    if (prevStatus === 'new' && ['packaging', 'ready'].includes(newStatus) && (photoControlEnabled || selectedOrder.channel_requires_photo) && !(selectedOrder.photos || []).length) {
      showToast('Добавьте фото собранного заказа — без него заказ дальше не передать', 'error');
      setEditForm(prev => ({ ...prev, status: prevStatus }));
      return;
    }
    try {
      if (onBeforeStatusChange) onBeforeStatusChange(orderId, newStatus, prevStatus);
      setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      setEditForm(prev => ({ ...prev, status: newStatus }));
      await api.updateOrder(orderId, { status: newStatus });
      // Перезагрузить заказ чтобы обновить status_history
      const updated = await api.getOrderById(orderId).catch(() => null);
      if (updated) {
        setSelectedOrder(updated);
        setEditForm(prev => ({ ...prev, status: updated.status }));
      }
      showToast('Статус обновлён');
      if (onOrderUpdated) onOrderUpdated();
    } catch (error) {
      if (error?.message?.includes('отменён')) {
        showToast('Заказ был отменён. Изменить статус запрещается.', 'error');
      } else {
        // Текст сервера — понятный: «Добавьте фото…», «Фото не ушло на Flowwow…» и т. п.
        showToast(error?.message || 'Ошибка обновления', 'error');
      }
      const updated = await api.getOrderById(orderId).catch(() => null);
      if (updated) {
        setSelectedOrder(updated);
        setEditForm(prev => ({ ...prev, status: updated.status }));
      }
      if (onOrderUpdated) onOrderUpdated();
    }
  };

  const handleSaveCancellation = async () => {
    if (!editForm.cancellationReason.trim()) {
      showToast('Укажите причину отмены', 'error');
      return;
    }
    try {
      if (onBeforeCancellation) onBeforeCancellation(selectedOrder.id);
      setSelectedOrder(prev => ({ ...prev, status: 'cancelled', cancellation_comment: editForm.cancellationReason }));
      setEditForm(prev => ({ ...prev, status: 'cancelled' }));
      const cancelData = {
        status: 'cancelled',
        cancellation_comment: editForm.cancellationReason,
      };
      if (selectedOrder.status === 'completed') {
        cancelData.return_to_stock = returnToStock;
      }
      await api.updateOrder(selectedOrder.id, cancelData);
      showToast('Заказ отменён');
      setPendingCancellation(false);
      // Перечитать заказ: оплата у отменённого тоже «Отменён», иначе висит «Ожидает · N мин» с кнопкой «Оплачен»
      const updated = await api.getOrderById(selectedOrder.id).catch(() => null);
      if (updated) setSelectedOrder(updated);
      if (onOrderUpdated) onOrderUpdated();
    } catch (error) {
      showToast('Ошибка отмены заказа', 'error');
      const updated = await api.getOrderById(selectedOrder.id).catch(() => null);
      if (updated) {
        setSelectedOrder(updated);
        setEditForm(prev => ({ ...prev, status: updated.status }));
      }
      if (onOrderUpdated) onOrderUpdated();
    }
  };

  // Комментарии и окно доставки правятся прямо в карточке; «Сохранить» внизу окна
  // появляется, только когда что-то изменили
  const commentsDirty = !isEditing && (
    (editForm.admin_comment || '') !== (selectedOrder.admin_comment || '')
    || (editForm.courier_comment || '') !== (selectedOrder.courier_comment || '')
    || (editForm.deliver_from || '') !== (selectedOrder.deliver_from || '')
    || (editForm.deliver_to || '') !== (selectedOrder.deliver_to || '')
  );
  const resetComments = () => setEditForm(prev => ({
    ...prev,
    admin_comment: selectedOrder.admin_comment || '',
    courier_comment: selectedOrder.courier_comment || '',
    deliver_from: selectedOrder.deliver_from || '',
    deliver_to: selectedOrder.deliver_to || '',
  }));
  const saveComments = async () => {
    if (windowInvalidRef.current) {
      showToast(windowInvalidRef.current, 'error');
      return;
    }
    setCommentsSaving(true);
    try {
      await api.updateOrder(selectedOrder.id, {
        admin_comment: editForm.admin_comment,
        courier_comment: editForm.courier_comment,
        deliver_from: editForm.deliver_from || '',
        deliver_to: editForm.deliver_to || '',
      });
      setSelectedOrder(prev => ({
        ...prev,
        admin_comment: editForm.admin_comment,
        courier_comment: editForm.courier_comment,
        deliver_from: editForm.deliver_from || null,
        deliver_to: editForm.deliver_to || null,
      }));
      showToast('Сохранено');
      if (onOrderUpdated) onOrderUpdated();
    } catch (error) {
      showToast(error?.message || 'Ошибка сохранения', 'error');
    } finally {
      setCommentsSaving(false);
    }
  };

  return (
    <>
      <Modal
        size="large"
        title={<>Заказ #{selectedOrder.order_type === 'subscription' ? `${selectedOrder.subscription_order_number || selectedOrder.subscription_id}/${selectedOrder.subscription_delivery_number}` : (selectedOrder.order_number || selectedOrder.id)}{selectedOrder.source_platform === 'flowwow' && selectedOrder.external_platform_order_id && <span style={{ fontWeight: 500, color: '#5E6561' }}> / {selectedOrder.external_platform_order_id}</span>}{selectedOrder.order_type === 'subscription' && <img src="/icons/nv-stats-subscriptions.svg" alt="" style={{ width: 18, height: 18, verticalAlign: '-3px', marginLeft: 6, color: '#1C3D2E' }} />}{selectedOrder.admin_order_type === 'client' && <span title="Создан админом" className="chip chip-admin"><img src="/icons/nv-admin.svg" alt="" />АДМ</span>}{selectedOrder.admin_order_type === 'internal' && <><span title="Создан админом" className="chip chip-admin"><img src="/icons/nv-admin.svg" alt="" />АДМ</span>{selectedOrder.is_marketing ? <span title="Маркетинговый заказ" className="chip chip-marketing">МАРК</span> : <span title="Внутренний заказ" className="chip chip-internal">ВНУТР</span>}</>}{selectedOrder.admin_order_type === 'walkin' && <><span title="Создан админом" className="chip chip-admin"><img src="/icons/nv-admin.svg" alt="" />АДМ</span><span title="Трафик" className="chip chip-walkin"><img src="/icons/nv-traffic.svg" alt="" />ТРАФИК</span></>} <span style={{ color: '#1C3D2E', fontWeight: 600, fontSize: 20 }}>· {formatDate(selectedOrder.order_date)}</span></>}
        onClose={onClose}
        // Открыта форма оплаты / доплаты / возврата — Escape и клик мимо спрашивают, а не закрывают карточку молча
        confirmOverlayClose={isEditing || (commentsDirty && 'Комментарии или окно доставки не сохранены. Закрыть без сохранения?')
          || ((showPaymentDialog || showAdjustmentDialog || noPaymentAdj) && 'Оплата не отмечена до конца. Закрыть карточку заказа?')}
        dirty={(isEditing && editTouched) || showPaymentDialog || !!showAdjustmentDialog || !!noPaymentAdj}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
            <div>
              {selectedOrder.source_platform === 'flowwow' ? (
                !['completed', 'cancelled'].includes(selectedOrder.status) && (
                  <span style={{ fontSize: 12, color: '#5E6561' }} title="Состав после оплаты Flowwow не меняется; время и адрес меняются на площадке и подтягиваются сюда автоматически">
                    Заказ Flowwow: не редактируется
                  </span>
                )
              ) : (!isEditing && !commentsDirty && !['completed', 'cancelled'].includes(selectedOrder.status) && (
                ['paid', 'paid_chat', 'not_required'].includes(selectedOrder.payment_status) ||
                (selectedOrder.order_type === 'subscription') ||
                (selectedOrder.admin_order_type != null)
              ) && (
                <button className="btn btn-secondary" onClick={() => setIsEditing(true)}>
                  Редактировать
                </button>
              ))}
            </div>
            {isEditing && (
              <div style={{ display: 'flex', gap: 8 }}>
                {/* Выйти из правки без сохранения — раньше только крестиком окна (UX-аудит 25.10) */}
                <button className="btn btn-secondary" disabled={editSaving} onClick={async () => {
                  if (editTouched && !(await nfConfirm('Изменения не сохранены. Выйти из правки без сохранения?', { confirmText: 'Выйти', cancelText: 'Остаться' }))) return;
                  setIsEditing(false);
                }}>Отмена</button>
                <button className="btn btn-primary" onClick={() => editPanelRef.current?.triggerSave()} disabled={editSaving}>
                  {editSaving ? 'Сохранение...' : 'Сохранить изменения'}
                </button>
              </div>
            )}
            {commentsDirty && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={resetComments} disabled={commentsSaving}>Отменить</button>
                <button className="btn btn-primary" onClick={saveComments} disabled={commentsSaving}>
                  {commentsSaving ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            )}
          </div>
        }
      >
        {isEditing ? (
          <div style={{ display: 'contents' }} onClickCapture={() => { if (!editTouched) setEditTouched(true); }}>
          <OrderEditPanel
            ref={editPanelRef}
            onSavingChange={setEditSaving}
            order={selectedOrder}
            onSave={async () => {
              try {
                const updated = await api.getOrderById(selectedOrder.id);
                applyServerOrder(updated);
              } catch {}
              setIsEditing(false);
              if (onOrderUpdated) onOrderUpdated();
            }}
            onCancel={() => setIsEditing(false)}
            showToast={showToast}
          />
          </div>
        ) : (
          <>
            {/* Статус — цветные pill-кнопки */}
            {(() => {
              // Единая палитра статусов заказа (orderStatus.js): у каждого статуса свой цвет;
              // кнопкам и ленте истории нужен насыщенный accent (у «У курьера» color светлый — для заливки)
              const statusColors = Object.fromEntries(Object.entries(ORDER_STATUS_COLORS)
                .map(([k, c]) => [k, { bg: c.bg, color: c.accent, activeBg: c.accent }]));
              // Иконки SVG (stroke=currentColor, наследуют цвет pill)
              const statusIcons = {
                new: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/></svg>,
                packaging: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7l9-4 9 4-9 4-9-4z"/><path d="M3 7v10l9 4 9-4V7"/></svg>,
                ready: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
                courier: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="7" width="15" height="10" rx="2"/><path d="M16 10h3l3 3v4h-6z"/><circle cx="5.5" cy="18.5" r="1.5"/><circle cx="18.5" cy="18.5" r="1.5"/></svg>,
                completed: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 4 12 14.01 9 11.01"/><polyline points="15 4 5 14.01 2 11.01"/></svg>,
                cancelled: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
              };
              return (
              <div style={{ marginBottom: 10 }}>
                {(() => {
                  // Линейка — из ядра статусов (status_steps): у самовывоза нет «У курьера», у заказа
                  // канала продаж — как решит канал (Flowwow: без «На упаковке»). «На упаковке» —
                  // ещё и только при режиме упаковки
                  const keys = Object.entries(statusLabels).filter(([key]) => {
                    const steps = selectedOrder.status_steps;
                    if (steps && !steps.includes(key)) return false;
                    if (key === 'packaging') return packerWorkflowEnabled;
                    if (!steps && key === 'courier') return selectedOrder.delivery_type !== 'pickup';
                    return true;
                  });
                  const pick = (key) => {
                    if (key === selectedOrder.status) return;
                    if (selectedOrder.status === 'cancelled') { showToast('Отменённый заказ нельзя изменить', 'error'); return; }
                    if (selectedOrder.status === 'completed' && key !== 'cancelled') { showToast('Завершённый заказ можно только отменить', 'error'); return; }
                    setEditForm(prev => ({ ...prev, status: key }));
                    if (key === 'cancelled') {
                      if (!editForm.cancellationReason && selectedOrder.cancellation_comment) setEditForm(prev => ({ ...prev, status: key, cancellationReason: selectedOrder.cancellation_comment }));
                      setPendingCancellation(true);
                    } else { setPendingCancellation(false); updateOrderStatus(selectedOrder.id, key); }
                  };
                  // Шкала статусов — один переключатель-ступеньки (UX-аудит 25.10): текущий шаг залит,
                  // пройденные — с галочкой, «Отменён» — отдельно справа красным контуром
                  const flow = keys.filter(([key]) => key !== 'cancelled');
                  const cur = flow.findIndex(([key]) => key === editForm.status);
                  const cancelled = editForm.status === 'cancelled';
                  return (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'stretch', marginBottom: selectedOrder.status_history?.length > 0 ? 8 : 0 }}>
                      <div className="seg order-steps" role="group" aria-label="Статус заказа">
                        {flow.map(([key, { label }], i) => {
                          const on = editForm.status === key;
                          const done = !cancelled && cur > i;
                          return (
                            <button key={key} type="button" aria-pressed={on} onClick={() => pick(key)}
                              className={`seg-btn${on ? ' on' : ''}${done ? ' done' : ''}`}>
                              {done ? statusIcons.ready : statusIcons[key]}
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      {keys.some(([key]) => key === 'cancelled') && (
                        <button type="button" aria-pressed={cancelled} onClick={() => pick('cancelled')}
                          className={`btn btn-sm ${cancelled ? 'btn-danger' : 'btn-danger-outline'}`} style={{ gap: 5 }}>
                          {statusIcons.cancelled}
                          {statusLabels.cancelled?.label || 'Отменён'}
                        </button>
                      )}
                    </div>
                  );
                })()}

                {/* Заказ канала продаж (Flowwow): статус меняется той же линейкой выше — площадку
                    обновляет сервер; здесь только действия канала: принять, «Везём сами», время, фото */}
                {selectedOrder.source_platform === 'flowwow' && (
                <div style={{ marginBottom: selectedOrder.status_history?.length > 0 ? 8 : 0 }}>
                  <FlowwowOrderPanel
                    order={selectedOrder}
                    showToast={showToast}
                    onUpdated={async () => {
                      const updated = await api.getOrderById(selectedOrder.id).catch(() => null);
                      if (updated) applyServerOrder(updated);
                      if (onOrderUpdated) onOrderUpdated();
                    }}
                  />
                </div>
                )}

                {/* Timeline — история статусов inline */}
                {selectedOrder.status_history?.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#999', flexWrap: 'wrap', padding: '4px 0' }}>
                    {selectedOrder.status_history.map((sh, idx) => {
                      const toLabel = statusLabels[sh.to_status]?.label || sh.to_status;
                      const sc = statusColors[sh.to_status] || statusColors.new;
                      const roleLabelsMap = { admin: 'Адм', florist: 'Фл', packer: 'Уп', courier: 'Кур' };
                      const who = sh.employee_name ? `${roleLabelsMap[sh.employee_role] || ''} ${sh.employee_name}` : 'Система';
                      const time = sh.changed_at ? new Date(sh.changed_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
                      return (
                        <React.Fragment key={sh.id}>
                          {idx > 0 && <div style={{ width: 20, height: 1, background: '#ddd', flexShrink: 0 }} />}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: sc.activeBg, flexShrink: 0 }} />
                            <span style={{ whiteSpace: 'nowrap' }}>
                              <span style={{ color: sc.color, fontWeight: 600 }}>{toLabel}</span>
                              <span style={{ color: '#bbb', marginLeft: 3 }}>{time}</span>
                              <span style={{ color: '#ccc', marginLeft: 3 }}>{who}</span>
                            </span>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}

                {selectedOrder.status === 'cancelled' && selectedOrder.cancellation_comment && !pendingCancellation && (
                  <div style={{ marginTop: 6, padding: '6px 10px', background: '#FFF4F2', '--ch': '5px', '--stroke': '#FFCEC7', border: '1px solid #FFCEC7' }}>
                    <div style={{ fontSize: 12, color: '#CB4D45' }}><strong>Причина:</strong> {selectedOrder.cancellation_comment}</div>
                  </div>
                )}

                {pendingCancellation && editForm.status === 'cancelled' && (
                  <div style={{ marginTop: 8, padding: '14px 16px', background: '#FFF4F2', '--ch': '8px', '--stroke': '#FFCEC7', border: '1px solid #FFCEC7' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#CB4D45' }}>Отмена заказа{selectedOrder.source_platform === 'flowwow' && ' — уйдёт отказ на Flowwow (штрафуется площадкой: первый в месяце предупреждение, дальше 100–150 % комиссии)'}</div>
                      <button onClick={() => { setPendingCancellation(false); setEditForm(prev => ({ ...prev, status: selectedOrder.status, cancellationReason: '' })); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: 18, lineHeight: 1, padding: '0 2px' }}>&times;</button>
                    </div>
                    <textarea rows="2" placeholder="Укажите причину отмены..."
                      value={editForm.cancellationReason} onChange={(e) => setEditForm(prev => ({ ...prev, cancellationReason: e.target.value }))}
                      style={{ width: '100%', resize: 'vertical', border: '1px solid #D8DDDA', '--ch': '6px', '--stroke': '#D8DDDA', padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                    {selectedOrder.status === 'completed' && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={returnToStock} onChange={(e) => setReturnToStock(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                        <span>Товар возвращён на склад</span>
                      </label>
                    )}
                    {/* [Назад] [Подтвердить отмену] справа; «Назад», а не «Отменить» — не путать с отменой заказа (UX-аудит 25.10) */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                      <button type="button" className="btn btn-secondary btn-md"
                        onClick={() => { setPendingCancellation(false); setEditForm(prev => ({ ...prev, status: selectedOrder.status, cancellationReason: '' })); }}>
                        Назад
                      </button>
                      <button type="button" className="btn btn-danger btn-md" onClick={handleSaveCancellation} disabled={!editForm.cancellationReason.trim()}>
                        Подтвердить отмену
                      </button>
                    </div>
                  </div>
                )}

                {/* Фото сборки — под статусами и их историей (владелец 25.10): без него «Собран» не ставится (у Flowwow
                    всегда — требование площадки, у остальных — при фото-контроле). Решение 08.10:
                    рядом со статусом, а не в «Оплате» — там форма оплаты и доплаты */}
                {(selectedOrder.channel_requires_photo || photoControlEnabled) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', margin: '12px 0 4px' }}>
                    <div style={{ minWidth: 150 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#1C3D2E', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Фото сборки</div>
                      <div style={{ fontSize: 12, color: '#5E6561', marginTop: 2 }}>
                        {selectedOrder.source_platform === 'flowwow' ? 'уходит покупателю при «Собран»' : 'нужно для «Собран»'}
                      </div>
                    </div>
                    <OrderPhotos order={selectedOrder} showToast={showToast} onUpdated={reloadOrder} />
                  </div>
                )}
              </div>
              );
            })()}

            {/* Создатель */}
            {selectedOrder.admin_order_type && selectedOrder.created_by_name && (
              <div style={{ marginBottom: 6, fontSize: 12, color: '#888', paddingLeft: 2 }}>
                Создал: <strong>{selectedOrder.created_by_name}</strong>
              </div>
            )}

            {/* Клиент + Доставка — две колонки */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              {/* Клиент */}
              <div style={SECTION}>
                <div style={SECTION_HEAD}>
                  {(selectedOrder.admin_order_type === 'walkin' || selectedOrder.admin_order_type === 'internal') ? 'Заказчик' : 'Клиент'}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {(() => {
                    const isWalkinOrInternal = selectedOrder.admin_order_type === 'walkin' || selectedOrder.admin_order_type === 'internal';
                    // Заказ Flowwow без аккаунта — имя покупателя с площадки или «Клиент»
                    const name = isWalkinOrInternal
                      ? (selectedOrder.customer_name || (selectedOrder.admin_order_type === 'internal' ? 'Администратор' : '—'))
                      : selectedOrder.source_platform === 'flowwow' && !selectedOrder.user
                        ? flowwowCustomerName(selectedOrder)
                        : `${selectedOrder.user?.first_name || ''} ${selectedOrder.user?.last_name || ''}`.trim();
                    const userId = selectedOrder.user?.id || selectedOrder.user_id;
                    // Кликабельно только для зарегистрированных клиентов (не walkin/internal)
                    if (!isWalkinOrInternal && userId) {
                      return (
                        <span
                          onClick={() => setClientModalId(userId)}
                          style={{ cursor: 'pointer', borderBottom: '1px dashed #1C3D2E', color: '#1C3D2E' }}
                          title="Открыть карточку клиента"
                        >{name}</span>
                      );
                    }
                    return <span>{name}</span>;
                  })()}
                  {selectedOrder.admin_order_type !== 'walkin' && selectedOrder.admin_order_type !== 'internal' && (() => {
                    const p = selectedOrder.source_platform
                      || (selectedOrder.user?.telegram_id ? 'telegram' : selectedOrder.user?.max_id ? 'max' : 'web');
                    const label = p === 'telegram' ? 'TG' : p === 'max' ? 'Max' : p === 'flowwow' ? 'Flowwow' : 'Web';
                    const cls = p === 'telegram' ? 'platform-tg' : p === 'max' ? 'platform-max' : p === 'flowwow' ? 'platform-flowwow' : 'platform-web';
                    const icon = p === 'flowwow' ? '/icons/flowwow.svg' : `/icons/nv-${p === 'telegram' ? 'tg' : p === 'max' ? 'max' : 'web'}.svg`;
                    return <span className={`platform-badge ${cls}`}><img src={icon} alt="" />{label}</span>;
                  })()}
                  {selectedOrder.order_type === 'subscription' && selectedOrder.subscription_payment_type && (
                    <span className={`entity-badge ${selectedOrder.subscription_payment_type === 'invoice' ? 'entity-yur' : 'entity-fiz'}`}>
                      {selectedOrder.subscription_payment_type === 'invoice' ? 'Юр' : 'Физ'}
                    </span>
                  )}
                </div>
                {selectedOrder.admin_order_type !== 'walkin' && selectedOrder.admin_order_type !== 'internal' && selectedOrder.user?.username && (
                  <div style={{ fontSize: 12, color: '#888' }}>@{selectedOrder.user?.username}</div>
                )}
                {selectedOrder.admin_order_type !== 'walkin' && selectedOrder.admin_order_type !== 'internal' && (selectedOrder.user?.telegram_id || selectedOrder.user?.max_id) && (
                  <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                    {selectedOrder.user?.telegram_id && <span>TG: {selectedOrder.user.telegram_id}</span>}
                    {selectedOrder.user?.telegram_id && selectedOrder.user?.max_id && <span> · </span>}
                    {selectedOrder.user?.max_id && <span>Max: {selectedOrder.user.max_id}</span>}
                  </div>
                )}
                {selectedOrder.phone ? (
                  <div style={{ fontSize: 13, marginTop: 2 }}>
                    <a href={phoneTelHref(selectedOrder.phone)} style={{ color: '#3A83BB', textDecoration: 'none' }}>{formatPhoneDisplay(selectedOrder.phone)}</a>
                  </div>
                ) : (selectedOrder.admin_order_type === 'walkin' && (
                  <div style={{ fontSize: 13, marginTop: 2, color: '#999' }}>—</div>
                ))}
                {selectedOrder.email && (
                  <div style={{ fontSize: 12 }}>
                    <a href={`mailto:${selectedOrder.email}`} style={{ color: '#3A83BB', textDecoration: 'none' }}>{selectedOrder.email}</a>
                  </div>
                )}
                {selectedOrder.order_type === 'subscription' && selectedOrder.subscription_payment_type === 'invoice' && (
                  <div style={{ marginTop: 4 }}>
                    {selectedOrder.subscription_company_file_url ? (
                      <button type="button" onClick={() => api.downloadPartnerCard(selectedOrder.subscription_id)} className="btn btn-secondary btn-sm">
                        Скачать карточку
                      </button>
                    ) : (
                      <span style={{ fontSize: 11, color: '#999' }}>Карточка не прикреплена</span>
                    )}
                  </div>
                )}
                {selectedOrder.source_title && (
                  <div style={{ fontSize: 12, color: '#5E6561', marginTop: 4 }}>
                    Откуда пришёл: <b style={{ color: '#1C3D2E' }}>{selectedOrder.source_title}</b>
                  </div>
                )}
                {selectedOrder.is_recipient_other && (
                  <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed #D8DDDA' }}>
                    <div style={{ fontSize: 11, color: '#1C3D2E', fontWeight: 700, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Получатель</div>
                    <div style={{ fontSize: 13 }}>
                      <strong>{selectedOrder.recipient_name}</strong>
                      {selectedOrder.recipient_phone && (
                        <span style={{ marginLeft: 6 }}><a href={phoneTelHref(selectedOrder.recipient_phone)} style={{ color: '#3A83BB', textDecoration: 'none', fontSize: 12 }}>{formatPhoneDisplay(selectedOrder.recipient_phone)}</a></span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Доставка */}
              <div style={SECTION}>
                {/* Курьер Flowwow для нас — самовывоз (забирает из магазина), но в заголовке — кто везёт */}
                {(() => {
                  const fwCourier = selectedOrder.source_platform === 'flowwow' && selectedOrder.delivery_type === 'pickup' && selectedOrder.external_delivery_type === 5;
                  const isPickup = selectedOrder.delivery_type === 'pickup' && !fwCourier;
                  return (
                    <div style={SECTION_HEAD}>
                      <img src={isPickup ? '/icons/nv-pickup.svg' : '/icons/nv-delivery.svg'} alt="" style={{ width: 14, height: 14 }} />
                      {fwCourier ? 'Курьер Flowwow' : isPickup ? 'Самовывоз' : 'Доставка'}
                    </div>
                  );
                })()}
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                  {selectedOrder.delivery_date ? selectedOrder.delivery_date.split('-').reverse().join('.') : '—'} <span style={{ color: '#666', fontWeight: 400 }}>·</span> <SlotChip order={selectedOrder} />
                </div>
                {/* Окно клиента внутри слота — редактируется ниже, здесь видно сразу при открытии.
                    У Flowwow не нужно: у своей доставки время покупателя и так стоит вместо слота */}
                {(selectedOrder.deliver_from || selectedOrder.deliver_to) && selectedOrder.source_platform !== 'flowwow' && (
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1C3D2E', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="square" strokeLinejoin="miter"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></svg>
                    Окно: {selectedOrder.deliver_from && selectedOrder.deliver_to
                      ? `${selectedOrder.deliver_from}–${selectedOrder.deliver_to}`
                      : (selectedOrder.deliver_from ? `не раньше ${selectedOrder.deliver_from}` : `не позже ${selectedOrder.deliver_to}`)}
                  </div>
                )}
                {/* Адрес одной строкой с переносом: «…ул Петра Гаврилова, д 9, кв. 6 · подъезд 2 · этаж 3 ·
                    Советский р-н». Район — только если его нет в самом адресе (у пригородов DaData
                    пишет «Пестречинский р-н» прямо в адресе — второй раз не повторяем) */}
                {(() => {
                  const addr = selectedOrder.delivery_address || '';
                  const extras = [
                    selectedOrder.entrance && `подъезд ${selectedOrder.entrance}`,
                    selectedOrder.floor && `этаж ${selectedOrder.floor}`,
                    selectedOrder.intercom && `домофон ${selectedOrder.intercom}`,
                  ].filter(Boolean);
                  const apt = selectedOrder.apartment ? `кв. ${selectedOrder.apartment}` : '';
                  const district = selectedOrder.city_district || '';
                  const showDistrict = district && !addr.toLowerCase().includes(district.toLowerCase());
                  // Всё одним стилем через «·»: адрес, кв., подъезд/этаж/домофон, район (замечание владельца 07.10)
                  const line = [[addr, apt].filter(Boolean).join(', '), ...extras, showDistrict && (/р-н|район/i.test(district) ? district : `${district} р-н`)]
                    .filter(Boolean).join(' · ');
                  return line ? <div style={{ fontSize: 13, marginTop: 2, lineHeight: 1.4 }}>{line}</div> : null;
                })()}
                {selectedOrder.hide_address_from_client && selectedOrder.delivery_type !== 'pickup' && (
                  <div style={{ fontSize: 12, color: '#B0603C', marginTop: 2 }} title="Заказчик в «Моих заказах» видит только «Доставка»">
                    Адрес скрыт от клиента
                  </div>
                )}
                {selectedOrder.delivery_address_dadata && selectedOrder.delivery_address_dadata !== selectedOrder.delivery_address && (
                  <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{selectedOrder.delivery_address_dadata}</div>
                )}
                {selectedOrder.delivery_address && selectedOrder.address_verified === false && (
                  <div style={{ marginTop: 4, padding: '4px 8px', background: '#F9EBCF', border: '1px solid #E4C177', '--ch': '4px', '--stroke': '#E4C177', fontSize: 12, color: '#9A4820' }}>
                    ⚠️ Проверить адрес
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#666', marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    {(() => {
                      const { label, iconSrc } = formatOrderPackaging(selectedOrder);
                      return iconSrc
                        ? <><img src={iconSrc} alt="" style={{ width: 14, height: 14 }} />{label}</>
                        : label;
                    })()}
                  </span>
                  {/* Стоимость доставки — только в итогах «Товаров», здесь не дублируем */}
                  {selectedOrder.vase_type && (
                    <span style={{
                      padding: '2px 8px', '--ch': '4px', '--stroke': (selectedOrder.vase_type === 'new_vase' ? '#1C3D2E' : '#ED956F'), fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: selectedOrder.vase_type === 'new_vase' ? '#DFF2E8' : '#FFE7DD',
                      color: selectedOrder.vase_type === 'new_vase' ? '#1C3D2E' : '#B0603C',
                      border: `1px solid ${selectedOrder.vase_type === 'new_vase' ? '#1C3D2E' : '#ED956F'}`
                    }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 4h8l-1 6a4 4 0 0 1-6 0z"/><path d="M9 10v8a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-8"/>
                      </svg>
                      {selectedOrder.vase_type === 'new_vase' ? 'Новая ваза' : 'Замена вазы'}
                    </span>
                  )}
                </div>
              </div>
            </div>


            {/* Оплата — нейтральный фон, primary green, без светофоров. На всю ширину: в ней
                раскрывается форма «Оплачен», а под ней — «Корректировки» (доплаты) */}
            <div style={{ marginBottom: 12, ...(['pending', 'expired', 'failed'].includes(selectedOrder.payment_status) ? SECTION_ATTN : SECTION) }}>
              <div style={SECTION_HEAD}>Оплата</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                {(() => {
                  const ps = selectedOrder.payment_status;
                  const isPaid = ps === 'paid' || ps === 'paid_chat';
                  const isPending = ps === 'pending';
                  const color = isPaid ? '#1C3D2E' : ps === 'cancelled' ? '#5E6561' : '#B0603C';
                  let label;
                  if (isPaid) {
                    let pm = paymentMethodLabel(selectedOrder.payment_method);
                    // «Счёт {бренд}» только при 100% бонусной оплате: total=0 + bonus_used>0.
                    // Частичное списание (total>0) — обычная оплата через ЮКассу/иной метод.
                    if (!pm && (selectedOrder.total || 0) === 0 && (selectedOrder.bonus_used || 0) > 0) pm = `Счёт ${BRAND_NAME}`;
                    label = pm ? `Оплачен · ${pm}` : 'Оплачен';
                  } else if (isPending && selectedOrder.order_date) {
                    // Для internal — нет дедлайна оплаты
                    if (selectedOrder.admin_order_type === 'internal') {
                      label = 'Ожидает';
                    } else {
                      const created = new Date(selectedOrder.order_date).getTime();
                      const expire = created + PAYMENT_EXPIRE_MINUTES * 60 * 1000;
                      const remainingMs = expire - Date.now();
                      if (remainingMs > 0) {
                        const mins = Math.ceil(remainingMs / 60000);
                        label = `Ожидает · ${mins} мин`;
                      } else {
                        label = 'Ожидает · истекает';
                      }
                    }
                  } else {
                    label = paymentStatusLabels[ps]?.label || 'Ожидает';
                  }
                  return <span style={{ fontSize: 14, fontWeight: 700, color }}>{label}</span>;
                })()}
                {selectedOrder.paid_at && (
                  <span style={{ fontSize: 13, color: '#5E6561' }}>
                    {new Date(selectedOrder.paid_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
                {selectedOrder.payment_accepted_by_name && (
                  <span style={{ fontSize: 13, color: '#1C3D2E' }}>
                    Принял: <strong>{selectedOrder.payment_accepted_by_name}</strong>
                  </span>
                )}
                {selectedOrder.payment_status === 'pending' && !showPaymentDialog && (
                  <button
                    type="button"
                    onClick={() => {
                      setPayAllSubscription(true);
                      setShowPaymentDialog(true);
                    }}
                    className="btn btn-secondary btn-sm"
                  >
                    Оплачен
                  </button>
                )}
              </div>

              {/* Комментарий к оплате — новое поле payment_comment, fallback на legacy скобки в payment_method */}
              {(() => {
                let cmt = (selectedOrder.payment_comment || '').trim();
                if (!cmt) {
                  const m = (selectedOrder.payment_method || '').match(/\(([^)]+)\)\s*$/);
                  if (m) cmt = m[1];
                }
                if (!cmt) return null;
                return (
                  <div style={{ fontSize: 13, color: '#5E6561', marginBottom: 8 }}>
                    Комментарий: <span style={{ color: '#1C3D2E' }}>{cmt}</span>
                  </div>
                );
              })()}

              <InlinePaymentDialog
                open={showPaymentDialog}
                onClose={() => setShowPaymentDialog(false)}
                extraSlot={selectedOrder.order_type === 'subscription' && selectedOrder.subscription_id ? (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer', fontSize: 13, color: '#1C3D2E' }}>
                    <input type="checkbox" checked={payAllSubscription} onChange={(e) => setPayAllSubscription(e.target.checked)} style={{ width: 16, height: 16, accentColor: '#1C3D2E' }} />
                    Оплатить все заказы подписки
                  </label>
                ) : null}
                onConfirm={async ({ method, paid_at, comment }) => {
                  if (selectedOrder.order_type === 'subscription' && selectedOrder.subscription_id && payAllSubscription) {
                    await api.updateSubscription(selectedOrder.subscription_id, {
                      payment_status: 'paid_chat',
                      payment_method: method,
                      payment_comment: comment,
                      paid_at,
                    });
                    showToast(`Все заказы подписки оплачены (${method})`);
                  } else {
                    await api.updateOrder(selectedOrder.id, { payment_status: 'paid_chat', payment_method: method, payment_comment: comment, paid_at });
                    showToast(`Оплата подтверждена (${method})`);
                  }
                  const refreshed = await api.getOrderById(selectedOrder.id).catch(() => null);
                  if (refreshed) setSelectedOrder(refreshed);
                  else setSelectedOrder(prev => ({ ...prev, payment_status: 'paid_chat', payment_method: method, payment_comment: comment, paid_at }));
                  setShowPaymentDialog(false);
                  if (onOrderUpdated) onOrderUpdated();
                }}
              />
              {/* paymentForm-state удалён вместе с inline-разметкой — теперь в InlinePaymentDialog */}
            </div>

            {/* Корректировки */}
            {selectedOrder.adjustments?.length > 0 && (
              <div style={{ marginBottom: 12, ...(selectedOrder.adjustments.some(a => a.status === 'pending') ? SECTION_ATTN : SECTION) }}>
                <div style={SECTION_HEAD}>Корректировки</div>
                {[...selectedOrder.adjustments].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(adj => {
                  const isPending = adj.status === 'pending';
                  const isResolved = adj.status === 'resolved';
                  const isRefund = adj.type === 'refund';
                  const typeColor = adj.type === 'surcharge' ? '#B0603C' : '#1C3D2E';
                  const statusColor = isPending ? '#B0603C' : isResolved ? '#1C3D2E' : '#5E6561';
                  const statusLabel = isPending ? 'Ожидает' : isResolved ? 'Выполнено' : 'Отменено';
                  return (
                  <div key={adj.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, padding: '10px 12px', background: '#fff', '--ch': '6px', '--stroke': (isPending ? '#ED956F' : '#D8DDDA'), marginBottom: 8, border: `1px solid ${isPending ? '#ED956F' : '#D8DDDA'}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, color: typeColor }}>
                          {adj.type === 'surcharge' ? `Доплата +${adj.amount.toLocaleString('ru-RU')} ₽` : `Возврат −${adj.amount.toLocaleString('ru-RU')} ₽`}
                        </span>
                        <span style={{ fontSize: 12, color: statusColor, fontWeight: 600, padding: '2px 8px', background: isPending ? '#FFE7DD' : isResolved ? '#DFF2E8' : '#F4F4EF', '--ch': '4px'}}>
                          {statusLabel}
                        </span>
                        {adj.payment_method && (
                          <span style={{ fontSize: 12, color: '#5E6561' }}>· {paymentMethodLabel(adj.payment_method)}</span>
                        )}
                        {isResolved && adj.resolved_by_name && (
                          <span style={{ fontSize: 12, color: '#1C3D2E' }}>
                            · {isRefund ? 'Вернул' : 'Принял'}: <strong>{adj.resolved_by_name}</strong>
                          </span>
                        )}
                      </div>
                      {(() => {
                        let cmt = (adj.comment || '').trim();
                        if (!cmt && adj.payment_method) {
                          const m = adj.payment_method.match(/\(([^)]+)\)\s*$/);
                          if (m) cmt = m[1];
                        }
                        if (!cmt) return null;
                        return (
                          <div style={{ marginTop: 4, fontSize: 12, color: '#5E6561' }}>
                            Комментарий: <span style={{ color: '#1C3D2E' }}>{cmt}</span>
                          </div>
                        );
                      })()}
                    </div>
                    {isPending && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            setShowAdjustmentDialog(adj);
                            setAdjForm({ method: '', paid_at_date: '', paid_at_time: '', comment: '', editTime: false, error: '' });
                          }}
                        >
                          {isRefund ? 'Оформить возврат' : 'Принять доплату'}
                        </button>
                        {adj.type === 'surcharge' && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              setNoPaymentAdj(adj);
                              setNoPaymentComment('');
                              setNoPaymentError('');
                            }}
                          >
                            Закрыть без доплаты
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-danger-outline btn-sm"
                          onClick={() => setCancelAdjConfirm(adj)}
                        >
                          {isRefund ? 'Отменить возврат' : 'Отменить доплату'}
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })}

                {showAdjustmentDialog && (
                  <div style={{ marginTop: 10, padding: '18px 20px', background: '#fff', '--ch': '10px', '--stroke': '#1C3D2E', border: '1px solid #1C3D2E', boxShadow: '0 2px 12px rgba(28,61,46,0.08)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1C3D2E', letterSpacing: '0.3px' }}>
                        {showAdjustmentDialog.type === 'surcharge' ? 'Приём доплаты' : 'Оформление возврата'} · {showAdjustmentDialog.amount.toLocaleString('ru-RU')} ₽
                      </div>
                      <button onClick={() => setShowAdjustmentDialog(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5E6561', fontSize: 20, lineHeight: 1, padding: '0 2px' }}>&times;</button>
                    </div>
                    {/* Способ оплаты — переключатель «одно из» (UX-аудит 25.10) */}
                    <div className="seg" role="group" aria-label="Способ оплаты" style={{ display: 'flex', width: '100%', marginBottom: 14 }}>
                      {(showAdjustmentDialog.type === 'refund'
                        ? [
                            { key: 'Карта', label: 'Карта' },
                            { key: 'Наличные', label: 'Наличные' },
                            { key: 'Р/с', label: 'Р/с' },
                          ]
                        : [
                            { key: 'QR', label: 'QR' },
                            { key: 'Карта', label: 'Карта' },
                            { key: 'Наличные', label: 'Наличные' },
                            { key: 'Р/с', label: 'Р/с' },
                          ]
                      ).map(m => {
                        const isActive = adjForm.method === m.key;
                        return (
                          <button
                            key={m.key} type="button" aria-pressed={isActive}
                            onClick={() => setAdjForm(prev => ({ ...prev, method: m.key, error: '' }))}
                            className={`seg-btn${isActive ? ' on' : ''}`}
                            style={{ flex: 1 }}
                          >
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                    {/* Дата/время — переключатель «Сейчас» / карандаш (вручную) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <div className="seg" role="group" aria-label="Время оплаты">
                        <button
                          type="button"
                          aria-pressed={!!adjForm.paid_at_date && !adjForm.editTime}
                          className={`seg-btn${adjForm.paid_at_date && !adjForm.editTime ? ' on' : ''}`}
                          onClick={() => {
                            const now = new Date();
                            const pad = (n) => String(n).padStart(2, '0');
                            setAdjForm(prev => ({
                              ...prev,
                              paid_at_date: `${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()}`,
                              paid_at_time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
                              editTime: false, error: '',
                            }));
                          }}
                        >
                          Сейчас
                        </button>
                        <button
                          type="button"
                          title="Указать вручную"
                          aria-label="Указать вручную"
                          aria-pressed={adjForm.editTime}
                          className={`seg-btn${adjForm.editTime ? ' on' : ''}`}
                          style={{ display: 'inline-flex', alignItems: 'center' }}
                          onClick={() => {
                            const now = new Date();
                            const pad = (n) => String(n).padStart(2, '0');
                            const nowDate = `${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()}`;
                            const nowTime = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
                            setAdjForm(prev => ({
                              ...prev, editTime: !prev.editTime,
                              paid_at_date: prev.editTime ? prev.paid_at_date : (prev.paid_at_date || nowDate),
                              paid_at_time: prev.editTime ? prev.paid_at_time : (prev.paid_at_time || nowTime),
                            }));
                          }}
                        >
                          <EditIcon size={14} />
                        </button>
                      </div>
                      {adjForm.paid_at_date && !adjForm.editTime && (
                        <span style={{ fontSize: 13, color: '#1C3D2E', fontWeight: 500 }}>{adjForm.paid_at_date}, {adjForm.paid_at_time}</span>
                      )}
                    </div>
                    {adjForm.editTime && (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        <input
                          type="text" inputMode="numeric" placeholder="ДД.ММ.ГГГГ"
                          value={adjForm.paid_at_date || ''}
                          onChange={(e) => {
                            let digits = e.target.value.replace(/\D/g, '');
                            if (digits.length > 8) digits = digits.slice(0, 8);
                            let formatted = '';
                            if (digits.length > 0) formatted += digits.slice(0, 2);
                            if (digits.length > 2) formatted += '.' + digits.slice(2, 4);
                            if (digits.length > 4) formatted += '.' + digits.slice(4, 8);
                            setAdjForm(prev => ({ ...prev, paid_at_date: formatted, error: '' }));
                          }}
                          style={{ flex: 1, padding: '8px 12px', border: '1px solid #D8DDDA', '--ch': '6px', '--stroke': '#D8DDDA', fontSize: 14, color: '#1C3D2E' }}
                        />
                        <input
                          type="text" inputMode="numeric" placeholder="ЧЧ:ММ"
                          value={adjForm.paid_at_time || ''}
                          onChange={(e) => {
                            let digits = e.target.value.replace(/\D/g, '');
                            if (digits.length > 4) digits = digits.slice(0, 4);
                            let formatted = '';
                            if (digits.length > 0) formatted += digits.slice(0, 2);
                            if (digits.length > 2) formatted += ':' + digits.slice(2, 4);
                            setAdjForm(prev => ({ ...prev, paid_at_time: formatted, error: '' }));
                          }}
                          style={{ width: 100, padding: '8px 12px', border: '1px solid #D8DDDA', '--ch': '6px', '--stroke': '#D8DDDA', fontSize: 14, color: '#1C3D2E' }}
                        />
                      </div>
                    )}
                    <input
                      type="text" value={adjForm.comment}
                      onChange={(e) => setAdjForm(prev => ({ ...prev, comment: e.target.value }))}
                      placeholder="Комментарий (необязательно)"
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #D8DDDA', '--ch': '6px', '--stroke': '#D8DDDA', fontSize: 13, marginBottom: 12, boxSizing: 'border-box', color: '#1C3D2E' }}
                    />
                    {adjForm.error && (
                      <div style={{ padding: '8px 12px', marginBottom: 10, background: '#FFE7DD', '--ch': '6px', '--stroke': '#ED956F', fontSize: 13, color: '#B0603C', border: '1px solid #ED956F' }}>
                        {adjForm.error}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-primary btn-md"
                        onClick={async () => {
                          if (!adjForm.method) { setAdjForm(prev => ({ ...prev, error: 'Выберите способ оплаты' })); return; }
                          if (!adjForm.paid_at_date || adjForm.paid_at_date.length < 10 || !adjForm.paid_at_time || adjForm.paid_at_time.length < 5) {
                            setAdjForm(prev => ({ ...prev, error: 'Введите дату и время' })); return;
                          }
                          const dateParts = adjForm.paid_at_date.split('.');
                          const isoDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
                          const resolvedAt = new Date(`${isoDate}T${adjForm.paid_at_time}`);
                          try {
                            await api.resolveAdjustment(selectedOrder.id, showAdjustmentDialog.id, {
                              status: 'resolved',
                              payment_method: adjForm.method,
                              resolved_at: resolvedAt.toISOString(),
                              comment: adjForm.comment.trim() || null,
                            });
                            showToast(showAdjustmentDialog.type === 'surcharge' ? 'Доплата принята' : 'Возврат выполнен');
                            setShowAdjustmentDialog(null);
                            const updated = await api.getOrderById(selectedOrder.id);
                            setSelectedOrder(updated);
                            if (onOrderUpdated) onOrderUpdated();
                          } catch (error) {
                            setAdjForm(prev => ({ ...prev, error: 'Ошибка сохранения' }));
                          }
                        }}
                      >
                        {showAdjustmentDialog.type === 'surcharge' ? 'Подтвердить приём' : 'Подтвердить возврат'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Диалог отмены корректировки */}
            {cancelAdjConfirm && (
              <div style={{ marginBottom: 12, padding: '14px 16px', background: '#FFE7DD', '--ch': '8px', '--stroke': '#ED956F', border: '1px solid #ED956F' }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, color: '#B0603C' }}>
                  Отменить корректировку?
                </div>
                <div style={{ fontSize: 13, color: '#1C3D2E', marginBottom: 12 }}>
                  {cancelAdjConfirm.type === 'surcharge'
                    ? `Отменить доплату +${cancelAdjConfirm.amount.toLocaleString('ru-RU')} ₽?`
                    : `Отменить возврат −${cancelAdjConfirm.amount.toLocaleString('ru-RU')} ₽?`}
                  {' '}Изменения состава заказа будут откатаны.
                </div>
                {/* [Назад] [Отменить …] справа; разрушительное — красным, без «Да/Нет» (UX-аудит 25.10) */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-md"
                    onClick={() => setCancelAdjConfirm(null)}
                  >
                    Назад
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-md"
                    onClick={async () => {
                      try {
                        await api.resolveAdjustment(selectedOrder.id, cancelAdjConfirm.id, {
                          status: 'cancelled',
                          payment_method: null,
                          resolved_at: new Date().toISOString(),
                        });
                        showToast('Корректировка отменена');
                        setCancelAdjConfirm(null);
                        const updated = await api.getOrderById(selectedOrder.id);
                        setSelectedOrder(updated);
                        if (onOrderUpdated) onOrderUpdated();
                      } catch {
                        showToast('Ошибка отмены корректировки', 'error');
                      }
                    }}
                  >
                    {cancelAdjConfirm.type === 'surcharge' ? 'Отменить доплату' : 'Отменить возврат'}
                  </button>
                </div>
              </div>
            )}

            {/* Диалог: закрыть корректировку без доплаты */}
            {noPaymentAdj && (
              <div style={{ marginBottom: 12, padding: '14px 16px', background: '#DFF2E8', '--ch': '8px', '--stroke': '#1C3D2E', border: '1px solid #1C3D2E' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1C3D2E', marginBottom: 8 }}>
                  Закрыть корректировку без доплаты
                </div>
                <div style={{ fontSize: 13, color: '#1C3D2E', marginBottom: 10 }}>
                  Доплата +{noPaymentAdj.amount.toLocaleString('ru-RU')} ₽ будет отмечена как «не требуется». Состав заказа останется текущим. Укажите причину — она сохранится в истории.
                </div>
                <input
                  type="text"
                  value={noPaymentComment}
                  onChange={(e) => { setNoPaymentComment(e.target.value); if (noPaymentError) setNoPaymentError(''); }}
                  placeholder="Причина (например: замена на дешёвый, по согласованию с клиентом)"
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #D8DDDA', '--ch': '6px', '--stroke': '#D8DDDA', fontSize: 13, marginBottom: 10, boxSizing: 'border-box', color: '#1C3D2E' }}
                />
                {noPaymentError && (
                  <div style={{ padding: '6px 10px', marginBottom: 8, background: '#FFE7DD', '--ch': '6px', '--stroke': '#ED956F', fontSize: 12, color: '#B0603C', border: '1px solid #ED956F' }}>
                    {noPaymentError}
                  </div>
                )}
                {/* [Отмена] [главная] справа (UX-аудит 25.10) */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-md"
                    onClick={() => { setNoPaymentAdj(null); setNoPaymentComment(''); setNoPaymentError(''); }}
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-md"
                    onClick={async () => {
                      if (!noPaymentComment.trim()) { setNoPaymentError('Укажите причину'); return; }
                      try {
                        await api.resolveAdjustment(selectedOrder.id, noPaymentAdj.id, {
                          status: 'resolved',
                          payment_method: 'Не требуется',
                          resolved_at: new Date().toISOString(),
                          comment: noPaymentComment.trim(),
                        });
                        showToast('Корректировка закрыта без доплаты');
                        setNoPaymentAdj(null);
                        setNoPaymentComment('');
                        const updated = await api.getOrderById(selectedOrder.id);
                        setSelectedOrder(updated);
                        if (onOrderUpdated) onOrderUpdated();
                      } catch {
                        setNoPaymentError('Ошибка сохранения');
                      }
                    }}
                  >
                    Закрыть без доплаты
                  </button>
                </div>
              </div>
            )}


            {/* История изменений */}
            {selectedOrder.history?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => setShowHistoryModal(true)}
                >
                  История изменений ({selectedOrder.history.length})
                </button>
              </div>
            )}

            {/* Товары */}
            {/* «Товары» — одна карточка: позиции, расчёт, полоса итога последней (padding у строк) */}
            <div style={{ marginBottom: 12, ...SECTION, padding: 0 }}>
              {/* «Нечётное количество» — пометкой справа в заголовке, а не отдельной строкой в составе */}
              <div style={{ ...SECTION_HEAD, margin: 0, justifyContent: 'space-between' }}>
                <span>Товары</span>
                {selectedOrder.odd_quantity && selectedOrder.items?.some(it => !it.bouquet_id) && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', '--ch': '4px',
                    background: '#fff', color: '#B0603C', fontSize: 11, fontWeight: 700,
                    textTransform: 'none', letterSpacing: 0,
                  }}>
                    <img src="/icons/nv-odd.svg" alt="" style={{ width: 13, height: 13 }} />
                    Нечётное количество
                  </span>
                )}
              </div>
              {selectedOrder.order_type === 'subscription' ? (
                <div style={{ padding: '12px 16px 14px' }}>
                  {(!selectedOrder.items || selectedOrder.items.length === 0) && fillItems.length === 0 && (
                    <div style={{ padding: '10px 14px', background: '#FFE7DD', border: '1px solid #ED956F', '--ch': '8px', '--stroke': '#ED956F', marginBottom: 8, fontSize: 14, color: '#B0603C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9"/><path d="M12 8v5"/><circle cx="12" cy="16.5" r="0.8" fill="currentColor"/>
                      </svg>
                      Заказ подписки ожидает наполнения
                    </div>
                  )}
                  {/* Существующие товары (inline редактирование) */}
                  {selectedOrder.items?.length > 0 && fillItems.length === 0 && !fillEditMode && (() => {
                    const baseItems = selectedOrder.items.filter(i => !i.is_extra);
                    const extraItems = selectedOrder.items.filter(i => i.is_extra);
                    return (
                    <>
                      {baseItems.map(item => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #EBEDEC' }}>
                          <div>
                            <div style={{ fontSize: 14, color: '#1C3D2E' }}>{item.product_name}</div>
                            <div style={{ fontSize: 12, color: '#5E6561' }}>{item.price?.toLocaleString('ru-RU')} ₽ × {item.quantity}</div>
                          </div>
                          <div style={{ fontWeight: 600, color: '#1C3D2E' }}>{(item.quantity * item.price).toLocaleString('ru-RU')} ₽</div>
                        </div>
                      ))}
                      {!['completed', 'cancelled'].includes(selectedOrder.status) && (
                        <button
                          onClick={async () => {
                            try {
                              // Получить доступность с учётом shelf_life и даты доставки
                              const baseOnly = selectedOrder.items.filter(i => !i.is_extra);
                              const productIds = baseOnly.map(i => i.product_id);
                              const maxQtyResult = await api.getAvailableMaxQtyBatch(
                                productIds, selectedOrder.delivery_date, selectedOrder.id
                              );
                              const mqMap = maxQtyResult.results || {};
                              setFillItems(baseOnly.map(i => ({
                                id: i.product_id,
                                name: i.product_name,
                                price: i.price,
                                quantity: i.quantity,
                                step: i.step || 1,
                                maxAvailable: mqMap[String(i.product_id)] || 0,
                              })));
                              setFillEditMode(true);
                            } catch {
                              const baseOnly = selectedOrder.items.filter(i => !i.is_extra);
                              setFillItems(baseOnly.map(i => ({
                                id: i.product_id,
                                name: i.product_name,
                                price: i.price,
                                quantity: i.quantity,
                                step: i.step || 1,
                                maxAvailable: 9999,
                              })));
                              setFillEditMode(true);
                            }
                          }}
                          type="button"
                          className="btn-link"
                          style={{ marginTop: 8 }}
                        >
                          Изменить состав
                        </button>
                      )}
                      {extraItems.length > 0 && (
                        <>
                          <div style={{ fontSize: 11, color: '#1C3D2E', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 12, marginBottom: 6, paddingTop: 10, borderTop: '1px solid #1C3D2E' }}>Дополнительные товары</div>
                          {extraItems.map(item => (
                            <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #EBEDEC' }}>
                              <div>
                                <div style={{ fontSize: 14, color: '#1C3D2E' }}>{item.product_name}</div>
                                <div style={{ fontSize: 12, color: '#5E6561' }}>{item.price?.toLocaleString('ru-RU')} ₽ × {item.quantity}</div>
                              </div>
                              <div style={{ fontWeight: 600, color: '#1C3D2E' }}>{(item.quantity * item.price).toLocaleString('ru-RU')} ₽</div>
                            </div>
                          ))}
                        </>
                      )}
                    </>
                    );
                  })()}
                  {/* Режим редактирования/наполнения */}
                  {(fillItems.length > 0 || (!selectedOrder.items?.length && !fillItems.length === false)) && fillItems.map((item, idx) => {
                    const maxQty = item.maxAvailable || 9999;
                    return (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #eee' }}>
                      <div>
                        <div style={{ fontSize: 14 }}>{item.name}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {item.price?.toLocaleString('ru-RU')} ₽
                          {maxQty < 9999 && <span style={{ color: '#999', marginLeft: 6 }}>· доступно {maxQty}</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {/* «− N +» — общий счётчик админки; шаг 1, от 1 до доступного (UX-аудит 25.10) */}
                        <QtyStepper
                          value={item.quantity}
                          min={1}
                          max={maxQty}
                          onChange={(val) => setFillItems(prev => prev.map((fi, i) => i === idx ? { ...fi, quantity: val } : fi))}
                        />
                        <button type="button" className="btn-icon is-danger" title="Убрать товар" aria-label="Убрать товар"
                          onClick={() => setFillItems(prev => prev.filter((_, i) => i !== idx))}>
                          <TrashIcon />
                        </button>
                      </div>
                    </div>
                    );
                  })}
                  {(fillItems.length > 0 || fillEditMode || !selectedOrder.items?.length) && (
                    <div style={{ marginTop: 8, position: 'relative' }}>
                      {/* Список открываем сразу, доступность догружаем следом: расчёт на 250+
                          товаров занимает секунды, и ждать его перед показом перечня незачем —
                          пока карта пуста, дропдаун показывает наличие из самих товаров. */}
                      <button onClick={() => {
                        setFillMaxQtyMap({});
                        setShowFillSearch(true);
                      }} type="button" className="btn-link">+ Добавить товар</button>
                      {showFillSearch && (
                        <ProductSearchDropdown
                          onProductsLoaded={async (products) => {
                            try {
                              const mqResult = await api.getAvailableMaxQtyBatch(
                                products.map(p => p.id), selectedOrder.delivery_date, selectedOrder.id
                              );
                              setFillMaxQtyMap(mqResult.results || {});
                            } catch { setFillMaxQtyMap({}); }
                          }}
                          onSelect={(product, availQty) => {
                            // availQty приходит из дропдауна: карта, если уже загружена, иначе наличие товара.
                            const maxAvail = availQty || 0;
                            setFillItems(prev => {
                              const existing = prev.find(i => i.id === product.id);
                              if (existing) return prev.map(i => i.id === product.id ? { ...i, quantity: Math.min(i.quantity + (product.step || 1), maxAvail) } : i);
                              return [...prev, { ...product, quantity: Math.min(product.step || 1, maxAvail), maxAvailable: maxAvail }];
                            });
                            setShowFillSearch(false);
                          }}
                          excludeIds={fillItems.map(i => i.id)}
                          onClose={() => setShowFillSearch(false)}
                          maxQtyMap={fillMaxQtyMap}
                          hideOutOfStock={true}
                        />
                      )}
                    </div>
                  )}
                  {(fillItems.length > 0 || fillEditMode) && (
                    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                      {(selectedOrder.items?.length > 0 || fillEditMode) && (
                        <button type="button" onClick={() => { setFillItems([]); setFillEditMode(false); }} className="btn btn-secondary btn-md">
                          Отмена
                        </button>
                      )}
                      <button type="button" onClick={handleFillOrder} disabled={isFilling} className="btn btn-primary btn-md">
                        {isFilling ? 'Сохранение...' : (fillEditMode ? 'Сохранить изменения' : 'Наполнить заказ')}
                      </button>
                    </div>
                  )}
                  {fillItems.length > 0 && (() => {
                    const cost = fillItems.reduce((s, i) => s + i.price * i.quantity, 0);
                    const fee = Math.round(cost * fillFeePercent / 100);
                    const total = cost + fee;
                    return (
                      <div style={{ marginTop: 8, padding: '8px 12px', background: '#DFF2E8', '--ch': '5px', '--stroke': '#1C3D2E', fontSize: 13, color: '#1C3D2E', border: '1px solid #1C3D2E' }}>
                        Стоимость + {fillFeePercent}% сервисный сбор: {total.toLocaleString('ru-RU')} ₽
                      </div>
                    );
                  })()}
                </div>
              ) : (
              <div className="order-items-list">
                {(() => {
                  // Ставки: стебли (охапка) по stems_pct; каталожные букеты И стебли
                  // сборных букетов — по bouquets_pct (все типы букетов = один процент).
                  const allItems = selectedOrder.items || [];
                  const catalogBouquets = [...allItems.filter(it => it.bouquet_id)]
                    .sort((a, b) => (b.price || 0) * (b.quantity || 0) - (a.price || 0) * (a.quantity || 0));
                  const looseStems = [...allItems.filter(it => !it.bouquet_id && it.assembly_sequence_no == null)]
                    .sort((a, b) => (b.price || 0) * (b.quantity || 0) - (a.price || 0) * (a.quantity || 0));
                  // Группировка стеблей сборных букетов по sequence_no
                  const asmMap = {};
                  allItems.forEach(it => {
                    if (it.assembly_sequence_no != null) {
                      const seq = it.assembly_sequence_no;
                      if (!asmMap[seq]) asmMap[seq] = { sequence_no: seq, florist_notes: it.assembly_florist_notes || null, items: [] };
                      asmMap[seq].items.push(it);
                    }
                  });
                  const customAssemblies = Object.values(asmMap).sort((a, b) => a.sequence_no - b.sequence_no);

                  // Состав столбиком во всю ширину строки: название · × кол-во · цена за шт · сумма.
                  // Суммы стоят в одном столбце с ценой букета: стебли вместе = цена букета,
                  // сервисный сбор — отдельной строкой в итогах
                  const compositionList = (parts, prefix) => (
                    <div style={{ marginTop: 8, marginLeft: 16 }}>
                      {prefix && <div style={{ fontSize: 12, color: '#909894', marginBottom: 2 }}>{prefix}</div>}
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto auto', columnGap: 12, rowGap: 2, fontSize: 13, lineHeight: 1.45, color: '#5E6561' }}>
                        {/* По стоимости позиции (цена × кол-во), от большей к меньшей — как в каталоге (и у старых заказов) */}
                        {[...parts].sort((x, y) => (y.sum - x.sum) || (y.unit - x.unit) || String(x.name).localeCompare(String(y.name), 'ru')).map(c => (
                          <React.Fragment key={c.key}>
                            {/* Точечная линия от названия к цифрам — видно, к какому цветку какая цена */}
                            <span style={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                              <span style={{ minWidth: 0 }}>
                                {c.replaced && <span title="Изменён" style={{ fontSize: 9, fontWeight: 700, padding: '1px 4px', marginRight: 4, '--ch': '3px', background: '#F9EBCF', color: '#7E5D00' }}>≠</span>}
                                {c.name}
                              </span>
                              <span aria-hidden="true" style={{ flex: 1, minWidth: 16, borderBottom: '1px dotted #C2C8C5', transform: 'translateY(-4px)' }} />
                            </span>
                            <span style={{ color: '#B0603C', fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' }}>× {c.qty}</span>
                            <span style={{ color: '#909894', textAlign: 'right', whiteSpace: 'nowrap', minWidth: 72 }}>{c.unit.toLocaleString('ru-RU')} ₽/шт</span>
                            <span style={{ color: '#1C3D2E', textAlign: 'right', whiteSpace: 'nowrap', minWidth: 72 }}>{c.sum.toLocaleString('ru-RU')} ₽</span>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  );

                  // kind: 'bouquet' (каталожный) | 'stem' (охапка)
                  // Цены строк — без сервисного сбора, сбор — отдельной строкой в итогах:
                  // строки + сбор + доставка = итого, ровно как клиент видит в «Моих заказах».
                  const renderItemRow = (item, kind) => {
                    const isBouquet = kind === 'bouquet';
                    const hasComponents = isBouquet && (item.bouquet_components?.length || 0) > 0;
                    const isAssembled = item.bouquet_kind === 'assembled';
                    const hasReplacement = isBouquet && item.bouquet_components?.some(c => c.is_replacement);
                    const baseLine = (item.price || 0) * (item.quantity || 0);
                    // «Своя цена»: цена букета задана вручную и ≠ сумме стеблей — цены стеблей
                    // показываем по средней (масштаб к цене букета), чтобы состав сходился с ценой
                    const componentsSum = hasComponents
                      ? item.bouquet_components.reduce((s, c) => s + (c.product_price || 0) * (c.quantity || 0), 0)
                      : 0;
                    const compScale = (componentsSum > 0 && (item.price || 0) !== componentsSum) ? ((item.price || 0) / componentsSum) : 1;
                    return (
                      <div key={item.id} className="order-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <div style={{ flex: 1 }}>
                            <div className="order-item-name" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span>{itemTitle(item)}</span>
                              {isBouquet && (
                                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', padding: '2px 6px', '--ch': '4px', background: '#FFE7DD', color: '#7E5D00' }}>{capitalizeFirst(singularLower(catalogLabels.bouquets))}</span>
                              )}
                              {isBouquet && (
                                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', padding: '2px 6px', '--ch': '4px', background: isAssembled ? '#DFF2E8' : '#F4F4EF', color: isAssembled ? '#1C3D2E' : '#5E6561' }}>
                                  {isAssembled ? 'Собран' : 'К сборке'}
                                </span>
                              )}
                              {kind === 'stem' && (
                                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', padding: '2px 6px', '--ch': '4px', background: '#DFF2E8', color: '#1C3D2E' }}>{catalogLabels.products}</span>
                              )}
                              {hasReplacement && (
                                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', padding: '2px 6px', '--ch': '4px', background: '#F9EBCF', color: '#7E5D00' }}>Состав изменён</span>
                              )}
                            </div>
                            <div className="order-item-qty">{item.quantity} шт × {item.price?.toLocaleString('ru-RU')} ₽</div>
                          </div>
                          <div style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{baseLine.toLocaleString('ru-RU')} ₽</div>
                        </div>
                        {hasComponents && compositionList(
                          item.bouquet_components.map(c => ({
                            key: c.id, name: c.product_name, qty: c.quantity, replaced: c.is_replacement,
                            unit: Math.round((c.product_price || 0) * compScale),
                            sum: Math.round((c.product_price || 0) * (c.quantity || 0) * compScale),
                          })),
                          item.quantity > 1 ? 'Состав на 1 шт:' : null,
                        )}
                      </div>
                    );
                  };

                  const sectionHeader = (text, divided = false) => (
                    <div style={{
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: '#909894', textTransform: 'uppercase',
                      padding: '10px 16px 4px', borderTop: divided ? '1px solid #EBEDEC' : 'none',
                    }}>{text}</div>
                  );

                  return (
                    <>
                      {catalogBouquets.length > 0 && (
                        <>
                          {sectionHeader(catalogLabels.bouquets)}
                          {catalogBouquets.map(it => renderItemRow(it, 'bouquet'))}
                        </>
                      )}
                      {customAssemblies.map(asm => {
                        // Сборный букет = единое целое: одна строка, состав столбиком под ней
                        const asmKey = `asm-${asm.sequence_no}`;
                        const base = asm.items.reduce((s, it) => s + (it.price || 0) * (it.quantity || 0), 0);
                        return (
                          <div key={asmKey} className="order-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                              <div style={{ flex: 1 }}>
                                <div className="order-item-name" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                  <span>Сборный {singularLower(catalogLabels.bouquets)} {asm.sequence_no}</span>
                                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', padding: '2px 6px', '--ch': '4px', background: '#FFE7DD', color: '#7E5D00' }}>Сборный</span>
                                </div>
                                {asm.florist_notes && (
                                  <div style={{ fontSize: 12, fontStyle: 'italic', color: '#5E6561', marginTop: 2 }}>{asm.florist_notes}</div>
                                )}
                              </div>
                              <div style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{base.toLocaleString('ru-RU')} ₽</div>
                            </div>
                            {compositionList(asm.items.map(it => ({
                              key: it.id, name: it.product_name, qty: it.quantity,
                              unit: it.price || 0, sum: (it.price || 0) * (it.quantity || 0),
                            })))}
                          </div>
                        );
                      })}
                      {looseStems.length > 0 && (
                        <>
                          {sectionHeader(catalogLabels.products, catalogBouquets.length > 0 || customAssemblies.length > 0)}
                          {looseStems.map(it => renderItemRow(it, 'stem'))}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              )}

              {selectedOrder.greeting && (
                <div style={{ margin: '8px 16px', padding: '10px 14px', background: '#DFF2E8', border: '1px solid #1C3D2E', '--ch': '8px', '--stroke': '#1C3D2E', fontSize: 14, color: '#1C3D2E' }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <img src="/icons/mail.svg" alt="" style={{ width: 16, height: 16 }} />
                    <span>Поздравление</span>
                  </div>
                  <div style={{ color: '#1C3D2E', lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'break-word' }}>{selectedOrder.greeting}</div>
                </div>
              )}

              {selectedOrder.order_type === 'subscription' ? (
                <div className="order-summary">
                  {(() => {
                    const allItems = selectedOrder.items || [];
                    const baseItems = allItems.filter(i => !i.is_extra);
                    const extraItems = allItems.filter(i => i.is_extra);
                    const fillCost = baseItems.reduce((s, i) => s + i.quantity * i.price, 0);
                    const subPrice = selectedOrder.subscription_price_per_delivery || 0;
                    const subDiscountPct = selectedOrder.subscription_discount_percent || 0;
                    const subDiscount = selectedOrder.subscription_discount_amount || Math.floor(subPrice * subDiscountPct / 100);
                    // Сервисный сбор только за подписочную часть (без extras).
                    // selectedOrder.service_fee хранит ОБЩИЙ сбор (sub + extras), поэтому считаем sub-часть отдельно.
                    const subFee = Math.floor((subPrice - subDiscount) * fillFeePercent / 100);
                    const extraCost = extraItems.reduce((s, i) => s + i.quantity * i.price, 0);
                    const extraFee = Math.floor(extraCost * fillFeePercent / 100);
                    const grandTotal = selectedOrder.total || 0;

                    return (
                      <>
                        {/* Себестоимость (для админа) */}
                        {fillCost > 0 && (() => {
                          const fillFee = Math.floor(fillCost * fillFeePercent / 100);
                          const calcDelivery = selectedOrder.calculated_delivery_cost || 0;
                          const fillTotal = fillCost + fillFee + calcDelivery;
                          const subTotalPaid = (subPrice - subDiscount) + subFee;
                          const costColor = fillTotal > subTotalPaid ? '#B0603C' : '#1C3D2E';
                          return (
                            <>
                              <div className="order-summary-row">
                                <span style={{ color: '#666' }}>Себестоимость подписки</span>
                                <span style={{ color: '#666' }}>{fillCost.toLocaleString('ru-RU')} ₽</span>
                              </div>
                              <div className="order-summary-row">
                                <span style={{ color: '#666' }}>Сервисный сбор ({fillFeePercent}%)</span>
                                <span style={{ color: '#666' }}>{fillFee.toLocaleString('ru-RU')} ₽</span>
                              </div>
                              {calcDelivery > 0 && (
                                <div className="order-summary-row">
                                  <span style={{ color: '#666' }}>Доставка</span>
                                  <span style={{ color: '#666' }}>{calcDelivery.toLocaleString('ru-RU')} ₽</span>
                                </div>
                              )}
                              <div className="order-summary-row" style={{ fontWeight: 600, marginBottom: 12 }}>
                                <span>Итого себестоимость подписки</span>
                                <span style={{ color: costColor, fontWeight: 700 }}>{fillTotal.toLocaleString('ru-RU')} ₽</span>
                              </div>
                            </>
                          );
                        })()}

                        {/* Подписка */}
                        <div className="order-summary-row" style={{ paddingTop: fillCost > 0 ? 12 : 0, borderTop: fillCost > 0 ? '1px solid #D8DDDA' : 'none' }}>
                          <span>Стоимость подписки</span>
                          <span>{subPrice.toLocaleString('ru-RU')} ₽</span>
                        </div>
                        <div className="order-summary-row">
                          <span>Сервисный сбор {selectedOrder.service_fee_percent != null ? `(${selectedOrder.service_fee_percent}%)` : ''}</span>
                          <span>{subFee.toLocaleString('ru-RU')} ₽</span>
                        </div>

                        {/* Доставка (если есть, для подписки тоже показываем) */}
                        {(selectedOrder.delivery_cost || 0) > 0 && (
                          <div className="order-summary-row">
                            <span>Доставка</span>
                            <span>{selectedOrder.delivery_cost.toLocaleString('ru-RU')} ₽</span>
                          </div>
                        )}

                        {/* Скидка подписки */}
                        {selectedOrder.subscription_discount_percent > 0 && (
                          <div className="order-summary-row">
                            <span>Скидка подписки {selectedOrder.subscription_discount_percent}%</span>
                            <span style={{ color: '#1C3D2E' }}>−{selectedOrder.subscription_discount_amount?.toLocaleString('ru-RU')} ₽</span>
                          </div>
                        )}

                        {/* Бонусы */}
                        {(selectedOrder.bonus_used || 0) > 0 && (
                          <div className="order-summary-row">
                            <span style={{ color: '#1C3D2E' }}>Со счёта {BRAND_NAME}</span>
                            <span style={{ color: '#1C3D2E' }}>−{selectedOrder.bonus_used.toLocaleString('ru-RU')} ₽</span>
                          </div>
                        )}

                        {/* Итог подписки */}
                        {(() => {
                          const subTotal = (subPrice - subDiscount) + subFee + (selectedOrder.delivery_cost || 0) - (selectedOrder.bonus_used || 0);
                          return (
                            <div className="order-summary-row" style={{ fontWeight: 600, marginBottom: extraItems.length > 0 ? 0 : 8 }}>
                              <span>Итого подписка</span>
                              <span>{subTotal.toLocaleString('ru-RU')} ₽</span>
                            </div>
                          );
                        })()}

                        {/* Доптовары */}
                        {extraItems.length > 0 && (
                          <>
                            <div className="order-summary-row" style={{ paddingTop: 12, borderTop: '1px solid #1C3D2E', color: '#1C3D2E', fontWeight: 700, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.04em' }}>
                              <span>Дополнительно</span>
                              <span></span>
                            </div>
                            {extraItems.map(item => (
                              <div key={item.id} className="order-summary-row" style={{ paddingLeft: 12 }}>
                                <span style={{ color: '#5E6561' }}>{item.product_name} ×{item.quantity}</span>
                                <span style={{ color: '#5E6561' }}>{(item.quantity * item.price).toLocaleString('ru-RU')} ₽</span>
                              </div>
                            ))}
                            <div className="order-summary-row" style={{ paddingLeft: 12 }}>
                              <span style={{ color: '#5E6561' }}>Сервисный сбор ({fillFeePercent}%)</span>
                              <span style={{ color: '#5E6561' }}>{extraFee.toLocaleString('ru-RU')} ₽</span>
                            </div>
                          </>
                        )}

                        {/* Итого */}
                        <div className="order-summary-row total">
                          <span>Итого</span>
                          <span>{grandTotal.toLocaleString('ru-RU')} ₽</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : (
              <div className="order-summary">
                {/* «Товары» — только когда позиций больше одной, иначе это повтор строки выше */}
                {(() => {
                  const its = selectedOrder.items || [];
                  const positions = its.filter(it => it.bouquet_id).length
                    + new Set(its.filter(it => !it.bouquet_id && it.assembly_sequence_no != null).map(it => it.assembly_sequence_no)).size
                    + its.filter(it => !it.bouquet_id && it.assembly_sequence_no == null).length;
                  return positions > 1 && (
                    <div className="order-summary-row">
                      <span>Товары</span>
                      <span>{(selectedOrder.subtotal || 0).toLocaleString('ru-RU')} ₽</span>
                    </div>
                  );
                })()}
                {selectedOrder.has_postcard && (
                  <div className="order-summary-row">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <img src="/icons/mail.svg" alt="" style={{ width: 14, height: 14 }} />
                      Открытка{selectedOrder.source_platform === 'flowwow' && !(selectedOrder.postcard_price > 0) && ' Flowwow (бесплатная, текст в поздравлении)'}
                    </span>
                    <span>{(selectedOrder.postcard_price || 0).toLocaleString('ru-RU')} ₽</span>
                  </div>
                )}
                {/* Сервисный сбор — слагаемое: товары + открытка + сбор + доставка − бонусы = итого.
                    Стебли — по stems_service_fee_percent, букеты — по bouquets_service_fee_percent. */}
                {(selectedOrder.service_fee || 0) > 0 && (() => {
                  const pcts = [];
                  if ((selectedOrder.stems_service_fee || 0) > 0 && selectedOrder.stems_service_fee_percent != null) pcts.push({ label: catalogLabels.products.toLowerCase(), pct: selectedOrder.stems_service_fee_percent });
                  if ((selectedOrder.bouquets_service_fee || 0) > 0 && selectedOrder.bouquets_service_fee_percent != null) pcts.push({ label: catalogLabels.bouquets.toLowerCase(), pct: selectedOrder.bouquets_service_fee_percent });
                  const hint = pcts.length === 1 ? `${pcts[0].pct} %` : pcts.map(p => `${p.label} ${p.pct} %`).join(', ');
                  return (
                    <div className="order-summary-row">
                      <span>Сервисный сбор{hint && <span style={{ color: '#909894' }}> · {hint}</span>}</span>
                      <span>{selectedOrder.service_fee.toLocaleString('ru-RU')} ₽</span>
                    </div>
                  );
                })()}
                {(selectedOrder.delivery_type !== 'pickup' || (selectedOrder.delivery_cost || 0) > 0) && (
                  <div className="order-summary-row">
                    <span>Доставка</span>
                    <span>{(selectedOrder.delivery_cost || 0).toLocaleString('ru-RU')} ₽</span>
                  </div>
                )}
                {selectedOrder.bonus_used > 0 && (
                  <div className="order-summary-row">
                    <span>Со счёта {BRAND_NAME}</span>
                    <span style={{ color: '#1F763B' }}>−{selectedOrder.bonus_used} ₽</span>
                  </div>
                )}
                {selectedOrder.client_payment_amount != null && selectedOrder.client_payment_amount !== selectedOrder.total && (
                  <div className="order-summary-row" style={{ color: '#9A4820', fontWeight: 600 }}>
                    <span>К оплате клиентом</span>
                    <span style={{ color: '#9A4820' }}>{selectedOrder.client_payment_amount.toLocaleString('ru-RU')} ₽</span>
                  </div>
                )}
                <div className="order-summary-row total">
                  <span>Итого</span>
                  <span>{(selectedOrder.total || 0).toLocaleString('ru-RU')} ₽</span>
                </div>
              </div>
              )}
            </div>

            {/* Назначение сотрудников */}
            <div style={{ marginBottom: 12, ...SECTION }}>
              <div style={SECTION_HEAD}>Назначение</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <div style={{ fontSize: 12, color: '#5E6561', marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <img src="/icons/nv-florist.svg" alt="" style={{ width: 14, height: 14 }} />
                    Флорист
                  </div>
                  <Select
                    value={editForm.florist_id || ''}
                    onChange={(val) => {
                      const v = val ? parseInt(val) : null;
                      setEditForm({ ...editForm, florist_id: v });
                      api.updateOrder(selectedOrder.id, { florist_id: v }).then(() => {
                        setSelectedOrder(prev => ({ ...prev, florist_id: v }));
                        showToast('Флорист назначен');
                        if (onOrderUpdated) onOrderUpdated();
                      }).catch(() => showToast('Ошибка', 'error'));
                    }}
                    options={[
                      { value: '', label: '— Не назначен —' },
                      ...florists.map(f => ({ value: f.id, label: f.name })),
                    ]}
                  />
                </div>
                {packerWorkflowEnabled && selectedOrder.source_platform !== 'flowwow' && (
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div style={{ fontSize: 12, color: '#5E6561', marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <img src="/icons/nv-package.svg" alt="" style={{ width: 14, height: 14 }} />
                      Упаковщик
                    </div>
                    <Select
                      value={editForm.packer_id || ''}
                      onChange={(val) => {
                        const v = val ? parseInt(val) : null;
                        setEditForm({ ...editForm, packer_id: v });
                        api.updateOrder(selectedOrder.id, { packer_id: v }).then(() => {
                          setSelectedOrder(prev => ({ ...prev, packer_id: v }));
                          showToast('Упаковщик назначен');
                          if (onOrderUpdated) onOrderUpdated();
                        }).catch(() => showToast('Ошибка', 'error'));
                      }}
                      options={[
                        { value: '', label: '— Не назначен —' },
                        ...packers.map(p => ({ value: p.id, label: p.name })),
                      ]}
                    />
                  </div>
                )}
                {selectedOrder.delivery_type !== 'pickup' && (
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div style={{ fontSize: 12, color: '#5E6561', marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <img src="/icons/nv-delivery.svg" alt="" style={{ width: 14, height: 14 }} />
                      Курьер
                    </div>
                    <Select
                      value={editForm.courier_id || ''}
                      onChange={(val) => {
                        const v = val ? parseInt(val) : null;
                        setEditForm({ ...editForm, courier_id: v });
                        api.updateOrder(selectedOrder.id, { courier_id: v }).then(() => {
                          setSelectedOrder(prev => ({ ...prev, courier_id: v }));
                          showToast('Курьер назначен');
                          if (onOrderUpdated) onOrderUpdated();
                        }).catch(() => showToast('Ошибка', 'error'));
                      }}
                      options={[
                        { value: '', label: '— Не назначен —' },
                        ...couriers.map(c => ({ value: c.id, label: c.name })),
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* «Заметка администратора» и «Курьеру» (окно внутри слота + комментарий) — две колонки,
                как «Клиент» и «Доставка». Сохраняются кнопкой «Сохранить» внизу окна */}
            {(() => {
              const isPickup = selectedOrder.delivery_type === 'pickup';
              // Своя доставка Flowwow: окно выбрал покупатель на площадке (у «согласовать» — внесено
              // в блоке Flowwow) — показываем как у всех заказов, но серым: меняется только там
              const windowLocked = selectedOrder.source_platform === 'flowwow';
              const showWindow = !isPickup && (!windowLocked || !!(selectedOrder.deliver_from || selectedOrder.deliver_to));
              // У самовывоза курьера нет — поле показываем, только если комментарий уже есть
              const showCourier = !isPickup || !!selectedOrder.courier_comment;
              const FIELD_LABEL = { fontSize: 12, color: '#5E6561', marginBottom: 4 };
              const TEXTAREA = { width: '100%', padding: '10px 12px', border: '1px solid #D8DDDA', '--ch': '6px', '--stroke': '#D8DDDA', fontSize: 14, resize: 'vertical', fontFamily: 'inherit' };

              let windowBlock = null;
              if (!showWindow) {
                windowInvalidRef.current = '';
              } else {
                // Окно клиента внутри слота: варианты с шагом 15 минут от начала до конца слота
                // (нативный <input type="time"> в Chrome — списки часов/минут, непонятно и неудобно)
                const slot = selectedOrder.delivery_time || '';
                const m = slot.match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
                const startMin = m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : 8 * 60;
                const endMin = m ? parseInt(m[3], 10) * 60 + parseInt(m[4], 10) : 22 * 60;
                const hhmm = (x) => `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`;
                const grid = [];
                for (let t = startMin; t <= endMin; t += 15) grid.push(hhmm(t));
                const from = (windowLocked ? selectedOrder.deliver_from : editForm.deliver_from) || '';
                const to = (windowLocked ? selectedOrder.deliver_to : editForm.deliver_to) || '';
                // Окно — просьба внутри слота, за его границы выходить нельзя: курьер
                // в это время всё равно не приедет, а VROOM получит невыполнимое окно.
                const toMin = (v) => (v ? parseInt(v.slice(0, 2), 10) * 60 + parseInt(v.slice(3, 5), 10) : null);
                const outside = (v) => !!(m && v && (toMin(v) < startMin || toMin(v) > endMin));
                // Сохранённое ранее окно могло оказаться вне слота, если слот заказа
                // потом поменяли. Подсветить — подсветим, но блокировать сохранение
                // нельзя: иначе в карточке не сохранить даже комментарий, пока не
                // тронешь чужое окно. Блокируем только то, что правят прямо сейчас
                const storedFrom = selectedOrder.deliver_from || '';
                const storedTo = selectedOrder.deliver_to || '';
                const outFrom = !windowLocked && outside(from);
                const outTo = !windowLocked && outside(to);
                const editedOut = (outFrom && from !== storedFrom) || (outTo && to !== storedTo);
                const bad = !windowLocked && !!(from && to && from >= to);
                const slotStr = m ? `${hhmm(startMin)}–${hhmm(endMin)}` : '';
                windowInvalidRef.current = editedOut
                  ? `Время вне слота ${slotStr} — окно задаётся внутри слота заказа`
                  : (bad ? '«Не раньше» позже, чем «не позже» — исправьте окно доставки' : '');
                let summary;
                if (outFrom || outTo) summary = `${outFrom && outTo ? 'Оба времени' : (outFrom ? '«Не раньше»' : '«Не позже»')} вне слота ${slotStr} — окно задаётся внутри слота`;
                else if (bad) summary = '«Не раньше» позже, чем «не позже» — окно пустое';
                else if (from && to) summary = `Клиент ждёт курьера между ${from} и ${to}`;
                else if (from) summary = `Клиент ждёт курьера не раньше ${from}`;
                else if (to) summary = `Клиент ждёт курьера не позже ${to}`;
                else summary = 'Окно не задано — курьер приедет в любое время слота';
                const sub = { fontSize: 11, color: '#909894', marginBottom: 3 };
                windowBlock = (
                  <div style={{ marginBottom: 14 }}>
                    <div style={FIELD_LABEL}
                         title={windowLocked
                           ? 'Окно выбрал покупатель на Flowwow — меняется только там и подтягивается сюда само'
                           : 'Просьба клиента внутри слота: маршрут строится с учётом окна, ETA позже «не позже» подсвечивается красным. Пусто = ограничения нет'}>
                      Окно доставки{m ? ` · слот ${slotStr}` : ''}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <label style={{ display: 'block', minWidth: 0 }}>
                        <div style={sub}>Не раньше</div>
                        <TimeCombo value={from} options={grid} style={{ width: '100%' }} invalid={outFrom || bad} disabled={windowLocked}
                          emptyLabel={m ? `с ${hhmm(startMin)}` : 'без ограничения'}
                          onChange={(v) => setEditForm({ ...editForm, deliver_from: v })} />
                      </label>
                      <label style={{ display: 'block', minWidth: 0 }}>
                        <div style={sub}>Не позже</div>
                        <TimeCombo value={to} options={grid} style={{ width: '100%' }} invalid={outTo || bad} disabled={windowLocked}
                          emptyLabel={m ? `до ${hhmm(endMin)}` : 'без ограничения'}
                          onChange={(v) => setEditForm({ ...editForm, deliver_to: v })} />
                      </label>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, fontSize: 12, color: (bad || outFrom || outTo) ? '#CB4D45' : '#5E6561', marginTop: 6 }}>
                      <span>{summary}</span>
                      {(from || to) && !windowLocked && (
                        <button type="button" className="btn-link" onClick={() => setEditForm({ ...editForm, deliver_from: '', deliver_to: '' })}
                          style={{ whiteSpace: 'nowrap' }}>
                          Очистить время
                        </button>
                      )}
                    </div>
                  </div>
                );
              }

              const courierLen = (editForm.courier_comment || '').length;
              return (
                <div style={{ display: 'grid', gridTemplateColumns: showCourier ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 12 }}>
                  <div style={{ ...SECTION, display: 'flex', flexDirection: 'column' }}>
                    <div style={SECTION_HEAD}>Заметка администратора</div>
                    <div style={FIELD_LABEL}>Видят только сотрудники</div>
                    <textarea
                      value={editForm.admin_comment || ''}
                      onChange={(e) => setEditForm({ ...editForm, admin_comment: e.target.value })}
                      placeholder="Заметка по заказу…"
                      rows={3}
                      style={{ ...TEXTAREA, flex: 1, minHeight: 80 }}
                    />
                  </div>
                  {showCourier && (
                    <div style={SECTION}>
                      <div style={SECTION_HEAD}>
                        <img src="/icons/nv-delivery.svg" alt="" style={{ width: 14, height: 14 }} />
                        Курьеру
                      </div>
                      {windowBlock}
                      <div style={{ ...FIELD_LABEL, display: 'flex', justifyContent: 'space-between' }}>
                        <span>Комментарий для курьера</span>
                        <span style={{ color: courierLen >= 85 ? '#CB4D45' : '#909894' }}>{courierLen}/100</span>
                      </div>
                      <textarea
                        value={editForm.courier_comment || ''}
                        onChange={(e) => setEditForm({ ...editForm, courier_comment: e.target.value.slice(0, 100) })}
                        placeholder="Домофон, «позвонить за 30 минут»…"
                        maxLength={100}
                        rows={3}
                        style={TEXTAREA}
                      />
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </Modal>

      {/* Модалка истории изменений */}
      {showHistoryModal && (
        <Modal
          title={`История изменений заказа #${selectedOrder.order_type === 'subscription' ? `${selectedOrder.subscription_order_number || selectedOrder.subscription_id}/${selectedOrder.subscription_delivery_number}` : (selectedOrder.order_number || selectedOrder.id)}`}
          onClose={() => setShowHistoryModal(false)}
          footer={<button className="btn btn-secondary" onClick={() => setShowHistoryModal(false)}>Закрыть</button>}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(selectedOrder.history || []).map(h => {
              let changes = [];
              try { changes = JSON.parse(h.changes); } catch {}
              const dt = h.created_at ? new Date(h.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
              const diff = h.adjustment_amount;
              // Привязанная корректировка (если изменение породило доплату/возврат)
              const relatedAdj = (selectedOrder.adjustments || []).find(a => a.history_id === h.id);
              const adjResolved = relatedAdj && relatedAdj.status === 'resolved';
              const adjCancelled = relatedAdj && relatedAdj.status === 'cancelled';
              return (
                <div key={h.id} style={{ padding: '12px 14px', background: '#F4F4EF', '--ch': '8px', borderLeft: '3px solid #1C3D2E' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#1C3D2E' }}>{h.employee_name || 'Admin'}</span>
                    <span style={{ fontSize: 12, color: '#5E6561' }}>{dt}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                    {changes.map((c, i) => (
                      <div key={i} style={{ fontSize: 13, color: '#1C3D2E', paddingLeft: 10, borderLeft: '2px solid #D8DDDA' }}>{c}</div>
                    ))}
                  </div>
                  {h.total_before != null && h.total_after != null && (
                    <div style={{ fontSize: 13, fontWeight: 700, color: diff > 0 ? '#B0603C' : diff < 0 ? '#1C3D2E' : '#5E6561' }}>
                      {h.total_before.toLocaleString('ru-RU')} ₽ → {h.total_after.toLocaleString('ru-RU')} ₽
                      {diff != null && diff !== 0 && (
                        <span> ({diff > 0 ? '+' : ''}{diff.toLocaleString('ru-RU')} ₽)</span>
                      )}
                    </div>
                  )}
                  {relatedAdj && (adjResolved || adjCancelled) && (
                    <div style={{ marginTop: 8, padding: '8px 10px', background: '#fff', '--ch': '5px', '--stroke': '#D8DDDA', border: '1px solid #D8DDDA', fontSize: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', color: adjResolved ? '#1C3D2E' : '#5E6561' }}>
                        <span style={{ fontWeight: 600 }}>
                          {adjCancelled
                            ? 'Корректировка отменена'
                            : `${relatedAdj.type === 'refund' ? 'Возврат' : 'Доплата'} выполнена`}
                        </span>
                        {adjResolved && relatedAdj.payment_method && (
                          <span style={{ color: '#5E6561' }}>
                            · {paymentMethodLabel(relatedAdj.payment_method)}
                          </span>
                        )}
                        {adjResolved && relatedAdj.resolved_by_name && (
                          <span style={{ color: '#5E6561' }}>
                            · {relatedAdj.type === 'refund' ? 'Вернул' : 'Принял'}: <strong style={{ color: '#1C3D2E' }}>{relatedAdj.resolved_by_name}</strong>
                          </span>
                        )}
                      </div>
                      {relatedAdj.comment && (
                        <div style={{ marginTop: 4, color: '#1C3D2E' }}>
                          <span style={{ color: '#5E6561' }}>Комментарий:</span> {relatedAdj.comment}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Modal>
      )}

      {clientModalId && (
        <ClientProfileModal
          clientId={clientModalId}
          onClose={() => setClientModalId(null)}
          showToast={showToast}
        />
      )}
    </>
  );
}

export default OrderModal;
